import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { useLang } from "@/context/LanguageContext";
import Reveal from "@/components/Reveal";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const inputClass =
  "w-full border-b border-gold/25 bg-transparent py-3 font-light text-parchment placeholder:text-sand/50 focus:border-gold focus:outline-none transition-colors duration-300";

const Inauguration = () => {
  const { t } = useLang();
  const [form, setForm] = useState({ name: "", email: "", guests: "", message: "" });
  const [sending, setSending] = useState(false);

  const set = (key) => (e) => setForm({ ...form, [key]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      await axios.post(`${API}/rsvp`, {
        ...form,
        guests: form.guests || t.inauguration.guests_options[0],
      });
      toast.success(t.inauguration.success);
      setForm({ name: "", email: "", guests: "", message: "" });
    } catch {
      toast.error(t.inauguration.error);
    } finally {
      setSending(false);
    }
  };

  return (
    <section id="inauguration" data-testid="inauguration-section" className="relative border-b border-gold/15 bg-ink2">
      <div className="mx-auto grid max-w-[1600px] grid-cols-1 gap-14 px-4 py-20 sm:px-8 lg:grid-cols-2 lg:px-16 lg:py-28">
        <div className="flex flex-col justify-center">
          <Reveal>
            <p className="font-mono text-[11px] uppercase tracking-[0.35em] text-gold">{t.inauguration.kicker}</p>
          </Reveal>
          <Reveal delay={0.1}>
            <p data-testid="inauguration-date" className="mt-6 font-serif text-6xl font-light leading-none text-parchment sm:text-7xl lg:text-8xl">
              01<span className="text-gold">·</span>11<span className="text-gold">·</span>2026
            </p>
          </Reveal>
          <Reveal delay={0.18}>
            <h2 className="mt-5 font-serif text-2xl font-light italic text-goldlight sm:text-3xl">
              {t.inauguration.title}
            </h2>
          </Reveal>
          <Reveal delay={0.26}>
            <p className="mt-6 max-w-md text-base font-light leading-relaxed text-sand sm:text-lg">
              {t.inauguration.sub}
            </p>
          </Reveal>
        </div>

        <Reveal delay={0.15}>
          <form
            data-testid="rsvp-form"
            onSubmit={submit}
            className="gold-frame self-center border border-gold/25 bg-card2 p-8 sm:p-10"
          >
            <p className="mb-8 font-mono text-[10px] uppercase tracking-[0.3em] text-gold">
              {t.inauguration.form_title}
            </p>
            <div className="space-y-7">
              <div className="grid grid-cols-1 gap-7 sm:grid-cols-2">
                <div>
                  <label htmlFor="rsvp-name" className="font-mono text-[10px] uppercase tracking-[0.3em] text-sand">
                    {t.inauguration.name}
                  </label>
                  <input
                    id="rsvp-name"
                    data-testid="rsvp-name-input"
                    required
                    value={form.name}
                    onChange={set("name")}
                    className={inputClass}
                  />
                </div>
                <div>
                  <label htmlFor="rsvp-email" className="font-mono text-[10px] uppercase tracking-[0.3em] text-sand">
                    {t.inauguration.email}
                  </label>
                  <input
                    id="rsvp-email"
                    data-testid="rsvp-email-input"
                    type="email"
                    required
                    value={form.email}
                    onChange={set("email")}
                    className={inputClass}
                  />
                </div>
              </div>
              <div>
                <label htmlFor="rsvp-guests" className="font-mono text-[10px] uppercase tracking-[0.3em] text-sand">
                  {t.inauguration.guests}
                </label>
                <select
                  id="rsvp-guests"
                  data-testid="rsvp-guests-select"
                  value={form.guests}
                  onChange={set("guests")}
                  className={`${inputClass} cursor-pointer bg-card2`}
                >
                  {t.inauguration.guests_options.map((g) => (
                    <option key={g} value={g} className="bg-card2 text-parchment">
                      {g}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label htmlFor="rsvp-message" className="font-mono text-[10px] uppercase tracking-[0.3em] text-sand">
                  {t.inauguration.message}
                </label>
                <textarea
                  id="rsvp-message"
                  data-testid="rsvp-message-textarea"
                  rows={3}
                  value={form.message}
                  onChange={set("message")}
                  className={`${inputClass} resize-none`}
                />
              </div>
              <button
                data-testid="rsvp-submit-button"
                type="submit"
                disabled={sending}
                className="group relative w-full overflow-hidden rounded-full border border-gold/60 px-10 py-4 font-mono text-[11px] uppercase tracking-[0.25em] text-gold transition-colors duration-500 hover:text-ink disabled:opacity-50"
              >
                <span className="absolute inset-0 -translate-x-full bg-gold transition-transform duration-500 ease-out group-hover:translate-x-0" />
                <span className="relative">{sending ? t.inauguration.sending : t.inauguration.send}</span>
              </button>
            </div>
          </form>
        </Reveal>
      </div>
    </section>
  );
};

export default Inauguration;
